import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import posthog from '../lib/posthog';
import { trackMetaEvent, setMetaUserData } from '../lib/meta';
import { getAdAttribution } from '../lib/adAttribution';
import { sendAdAttribution, sendCAPIEvent } from '../lib/api';
import { resetAllSwrStores } from '../stores/swr/registry';

export function useSupabaseAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsubscribe;

    const init = async () => {
      setLoading(true);
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      const sessionUser = data.session?.user ?? null;
      setUser(sessionUser);
      setLoading(false);

      const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
        const nextUser = session?.user ?? null;

        switch (event) {
          case 'SIGNED_IN':
            setUser(nextUser);
            if (nextUser) {
              posthog.identify(nextUser.id, { email: nextUser.email });
              const fullName = nextUser.user_metadata?.full_name || nextUser.user_metadata?.name || '';
              const [fn, ...lnParts] = fullName.split(' ');
              setMetaUserData({
                em: nextUser.email,
                external_id: nextUser.id,
                fn: fn || undefined,
                ln: lnParts.join(' ') || undefined,
              });

              const isNewUser = nextUser.created_at && nextUser.last_sign_in_at &&
                Math.abs(new Date(nextUser.created_at) - new Date(nextUser.last_sign_in_at)) < 5000;
              if (isNewUser) {
                localStorage.setItem(`latted_needs_post_signup:${nextUser.id}`, 'true');
              }
              if (isNewUser) {
                posthog.capture('user signed up', {
                  method: nextUser.app_metadata?.provider || 'email',
                });
                const regEventId = trackMetaEvent('CompleteRegistration', {
                  content_name: nextUser.app_metadata?.provider || 'email',
                  status: 'registered',
                });
                sendCAPIEvent('CompleteRegistration', regEventId, {
                  content_name: nextUser.app_metadata?.provider || 'email',
                  status: 'registered',
                }, getAdAttribution()).catch(() => {});
              } else {
                posthog.capture('user signed in', {
                  method: nextUser.app_metadata?.provider || 'email',
                });
              }

              // Persist ad attribution data to backend (include name for CAPI)
              const attribution = getAdAttribution();
              if (fn) attribution.firstName = fn;
              if (lnParts.join(' ')) attribution.lastName = lnParts.join(' ');
              sendAdAttribution(attribution).catch(() => {});
            }
            break;
          case 'TOKEN_REFRESHED':
          case 'INITIAL_SESSION':
            setUser(nextUser);
            if (nextUser) {
              const isNewUser = nextUser.created_at && nextUser.last_sign_in_at &&
                Math.abs(new Date(nextUser.created_at) - new Date(nextUser.last_sign_in_at)) < 5000;
              if (isNewUser) {
                localStorage.setItem(`latted_needs_post_signup:${nextUser.id}`, 'true');
              }
              posthog.identify(nextUser.id, { email: nextUser.email });
              // Persist fresh attribution for returning users who clicked an ad
              // Only send if there's real ad attribution (not just _fbp which always exists)
              const initialAttrib = getAdAttribution();
              const hasAdAttrib = initialAttrib.fbclid || initialAttrib.fbc
                || initialAttrib.utmSource || initialAttrib.utmMedium || initialAttrib.utmCampaign;
              if (hasAdAttrib) {
                const name = nextUser.user_metadata?.full_name || nextUser.user_metadata?.name || '';
                const [firstName, ...lastParts] = name.split(' ');
                if (firstName) initialAttrib.firstName = firstName;
                if (lastParts.join(' ')) initialAttrib.lastName = lastParts.join(' ');
                sendAdAttribution(initialAttrib).catch(() => {});
              }
            }
            break;
          case 'SIGNED_OUT':
            setUser(null);
            posthog.reset();
            resetAllSwrStores();
            break;
          default:
            setUser(nextUser);
            break;
        }

        setLoading(false);
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    };

    init();

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const signInWithGoogle = useCallback(() => {
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined;
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
  }, []);

  const signOut = useCallback(() => supabase.auth.signOut(), []);
  const signInWithEmail = useCallback((email, password) => (
    supabase.auth.signInWithPassword({ email, password })
  ), []);

  return useMemo(
    () => ({ user, loading, signInWithGoogle, signInWithEmail, signOut }),
    [loading, signInWithEmail, signInWithGoogle, signOut, user]
  );
}
